<template>
    <div class="d-bankid text-center">
        <div v-if="debug_mode" class="m-2 p-2">
            <div class="p-2 border">ServiceType: {{ ServiceType }}</div>
            <div class="p-2 border">Mode: {{ auth_mode }}</div>
            <div class="p-2 border">IsMobile: {{ is_mobile }}</div>
            <div class="p-2 border">Loading: {{ loading }}</div>
            <div class="p-2 border">Collecting: {{ is_collecting }}</div>
            <div class="p-2 border">DeviceOpen: {{ is_device_open }}</div>
            <div class="p-2 border">Complete: {{ is_complete }}</div>
            <div class="p-2 border">Failed: {{ is_failed }}</div>
            <div class="p-2 border">TimeLeft: {{ timeleft }}s</div>
        </div>

        <div v-if="!initiated || loading">
            <LoaderLottie v-if="animation_loading" :name="animation_loading" :size="size"></LoaderLottie>
        </div>

        <div v-if="is_collecting" class="sign-step-div">
            <div v-if="is_device_open">
                <d-alert variant="info">
                    <d-text>{{ device_open_text }}</d-text>
                </d-alert>
            </div>
            <div v-else>
                <div v-if="is_mobile">
                    <div v-if="data?.AutoStartToken" class="my-3">
                        <a :href="StartTokenHref" target="blank" class="btn btn-primary">
                            <d-row class="g-0 align-items-center">
                                <d-col cols="auto">
                                    <div class="me-2" v-html="buttonicon" style="width:24px;height:24px;"></div>
                                </d-col>
                                <d-col cols="auto"><d-text>{{ open_this_device_text }}</d-text> </d-col>
                            </d-row>
                        </a>
                    </div>
                </div>

                <d-qr-viewer v-if="ShowQR" :QRType="data.QRType" :value="data.QR" :size="size"></d-qr-viewer>

                <div v-if="is_mobile">
                    <span v-if="ShowQR" @click="ForceHideQR()" class="btn btn-link">
                        <d-text>Sign_HideQR</d-text>
                    </span>
                    <span v-else @click="ForceShowQR()" class="btn btn-link">
                        <d-text>Sign_ShowQR</d-text>
                    </span>
                </div>

                <div v-if="!is_mobile">
                    <div v-if="data?.AutoStartToken" class="my-3">
                        <a :href="StartTokenHref" target="blank" class="btn btn-primary">
                            <d-row class="g-0 align-items-center">
                                <d-col cols="auto">
                                    <div class="me-2" v-html="buttonicon" style="width:24px;height:24px;"></div>
                                </d-col>
                                <d-col cols="auto"><d-text>{{ open_this_device_text }}</d-text> </d-col>
                            </d-row>
                        </a>
                    </div>
                </div>              
            </div>

            <div v-if="display_timer && timeleft > 0" class="timer">
                    {{ ddate.secondsToTime(timeleft) }}
            </div>

            <div class="mt-2">
                <span @click="CancelAuth()" class="btn btn-link">
                    <d-text>Button_Cancel</d-text>
                </span>
            </div>

        </div>

        <div v-else-if="is_complete && completed_signing" class="sign-step-div">
            <SuccessLottie @complete="emitComplete" v-if="animation_success" :name="animation_success" :size="size">
            </SuccessLottie>
        </div>

        <div v-else-if="is_failed" class="sign-step-div">
            <d-alert variant="danger">
                <d-text>{{ failed_text }}</d-text>
            </d-alert>

            <div class="mt-4">
                <span @click="CancelAuth()" class="btn btn-link">
                    <d-text>Button_Cancel</d-text>
                </span>
                <d-button @click="Retry()" class="mx-2" variant="outline-primary">
                    <d-text>Button_Try_Again</d-text>
                </d-button>
            </div>
        </div>

    </div>
</template>

<script setup>

import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance, watch, defineProps, defineEmits } from 'vue'
import dicons from './../enums/dicons';

const emit = defineEmits(['bankid-complete', 'bankid-cancel'])

import LoaderLottie from "./Lottie/DLottieLoader.vue"
import SuccessLottie from "./Lottie/DLottieSuccess.vue"

const props = defineProps({
    debug_mode: { type: Boolean, default: false },
    ServiceType: { type: String, default: "BANKID_SE" },
    auth_url: { type: String, required: true },
    cancel_url: { type: String, required: true },
    is_sign: { type: Boolean, default: false },
    headers: { type: Object },
    ssnr: { type: String },
    userId: { type: String },    
    customPostData: { type: Object, default: () => {} },
    animation_loading: { type: String },
    animation_success: { type: String },
    display_timer: {type:Boolean, default: true}
})

import { useWebSocketStore } from '@/dod/store/dwebsocket'
import { useSecurityStore } from '@/dod/store/dsecurity';
const wsstore = useWebSocketStore();
const secStore = useSecurityStore();
// globals
const { appContext } = getCurrentInstance()
const deidentitet = appContext.config.globalProperties.$deidentitet;
const dbrowser = appContext.config.globalProperties.$dbrowser;
const ddate = appContext.config.globalProperties.$ddate;

// data 
const initiated = ref(false);
const loading = ref(true);
const mobile_init_complete = ref(false);
const completed_signing = ref(false);
const ForceQR = ref(false);
const data = ref({});

//const is_aborted = ref(false);
const AuthResponse = ref({});
const completeData = ref({});

// Computed property using computed()    
const is_mobile = computed(() => dbrowser.isMobile())
const is_collecting = computed(() => data.value?.CollectStatusHolder?.CollectStatusString == "PENDING")
const is_complete = computed(() => data.value?.CollectStatusHolder?.CollectStatusString == "COMPLETE")
const is_failed = computed(() => data.value?.CollectStatusHolder?.CollectStatusString == "FAILED")
const is_device_open = computed(() => data.value?.CollectStatusHolder?.CollectStateString == "DEVICE_OPEN")
const auth_mode = computed(() => props.is_sign ? "SIGNATURE" : "AUTHENTICATE")
const timeleft = computed(() => data.value?.TimeLeft ?? -1)
//const wsid = computed(() => wsstore.id);
const wsmessage = computed(() => wsstore.message)
const failed_text = computed(() => {

    var state = data.value?.CollectStatusHolder?.CollectStateString ?? "";
    switch (state) {
        case "NOT_OPENED":
            return "Sign_Not_Started_Timeout";
        case "CANCELED":
            return "Sign_Canceled";
        case "TIMEOUT":
            return "Sign_Timeout";
        default:
            return "Sign_Default_Error";
    }
})
const device_open_text = computed(() => props.ServiceType + "_Device_Open_Text")
const open_this_device_text = computed(() => props.ServiceType + "_Open_This_Device")
const ShowQR = computed(() => {
    if (!is_mobile.value || ForceQR.value) {
        if (data.value.QR)
            return true;
        return false;
    }
    return false;
})
const size = computed(() => is_mobile.value ? 100 : 200)
const StartTokenHref = computed(() => {
    var redirect = encodeURIComponent(window.location.href);
    if (props.ServiceType == "BANKID_SE")
        return is_mobile.value ? "https://app.bankid.com/?autostarttoken=" + data.value?.AutoStartToken + "&redirect=" + redirect : "bankid:///?autostarttoken=" + data.value?.AutoStartToken + "&redirect=null";
    return false;
})

const buttonicon = computed(() => {
    if (props.ServiceType == "BANKID_SE")
        return dicons.bankid_icons.button;
    return "";
})


// FUNCTIONS

function Init() {
    StartAuth(false);
}

function StartAuth() {
    if (is_mobile.value)
        mobile_init_complete.value = true;
    var post_data = {
        WebSocketId: wsstore.id,
        IdentificationNumber: props.ssnr,
        UserId: props.userId,
        CountryCode: "SE",
    };
    post_data = { ...post_data, ...props.customPostData };
    fetch(props.auth_url, { method: 'POST', body: JSON.stringify(post_data), headers: props.headers ?? {} });
}

function Retry() {
    Init();
}

function CancelAuth() {
    emit("bankid-cancel")
    DoCancelAuth();
}

function DoCancelAuth() {
    var post_data = {
        SessionId: data.value?.sessionId
    }
    fetch(props.cancel_url, { method: 'POST', body: JSON.stringify(post_data), headers: props.headers });
    deidentitet.clear();
}

function ReturnCollect() {
    loading.value = true;
    initiated.value = true;
}

function ForceShowQR() {
    ForceQR.value = true;
}

function ForceHideQR() {
    ForceQR.value = false;
}

function SetComplete(complete_data) {
    var response = complete_data.data;    
    if (response.Token)
        secStore.setToken(response.Token);
    if (response.User){
        secStore.setUser(response.User);
        secStore.addStoredUser(response.User);
    }
    initiated.value = true;
    completed_signing.value = true;
    loading.value = false;
    completeData.value = response;
    if (!props.animation_success)
        emitComplete();
}

function emitComplete() {
    emit("bankid-complete", completeData.value);
}

// watch
watch(wsmessage, (newValue) => {
    var messageType = newValue.type;
    switch (messageType) {
        case "collect_started":
        case "collect_update":
        case "collect_failed":
        case "collect_complete":
            if (messageType == "collect_complete") {
                loading.value = true;
                deidentitet.clear();
            } else if (messageType == "collect_failed") {
                deidentitet.clear();
                loading.value = false;
            } else if (messageType == "collect_started") {
                deidentitet.clear();
                deidentitet.setEIdentitetData({
                    AuthResponse: newValue.data,
                    //Client: client
                });
                loading.value = false;
            }
            initiated.value = true;
            data.value = newValue.data;
            break;
        case "auth_complete":
            loading.value = false;
            deidentitet.clear();            
            SetComplete(newValue);
            break;
        case "sign_error":
            deidentitet.clear();
            break;
    }
});


// onMounted lifecycle hook
onMounted(() => {
    if (deidentitet.hasEIdentitetData()) {
        var edata = deidentitet.getEIdentitetData();
        mobile_init_complete.value = true;
        AuthResponse.value = edata.AuthResponse;
        ReturnCollect();
        deidentitet.clear();
    } else {
        Init();
    }
})

onBeforeUnmount(() => {
    DoCancelAuth();
})

</script>

<style></style>